import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectNotificationsUnreadCountFromState } from 'src/app/Notifications/redux/notifications.selectors'
import { SidenavContentExpandedItemLinkProps } from 'src/components/SidenavContentExpandedItemLink/SidenavContentExpandedItemLink'
import InsightsActive from 'src/components/Svgs/InsightsActive'
import InsightsInactive from 'src/components/Svgs/InsightsInactive'
import SvgAiAgentActive from 'src/components/Svgs/SvgAiAgentActive'
import SvgAiAgentInactive from 'src/components/Svgs/SvgAiAgentInactive'
import SvgSidenavAccountingActive from 'src/components/Svgs/SvgSidenavAccountingActive'
import SvgSidenavAccountingInactive from 'src/components/Svgs/SvgSidenavAccountingInactive'
import SvgSidenavBudgetsActive from 'src/components/Svgs/SvgSidenavBudgetsActive'
import SvgSidenavBudgetsInactive from 'src/components/Svgs/SvgSidenavBudgetsInactive'
import SvgSidenavCardsActive from 'src/components/Svgs/SvgSidenavCardsActive'
import SvgSidenavCardsInactive from 'src/components/Svgs/SvgSidenavCardsInactive'
import SvgSidenavExpensesActive from 'src/components/Svgs/SvgSidenavExpensesActive'
import SvgSidenavExpensesInactive from 'src/components/Svgs/SvgSidenavExpensesInactive'
import SvgSidenavHomeActive from 'src/components/Svgs/SvgSidenavHomeActive'
import SvgSidenavHomeInactive from 'src/components/Svgs/SvgSidenavHomeInactive'
import SvgSidenavManagerReviewActive from 'src/components/Svgs/SvgSidenavManagerReviewActive'
import SvgSidenavManagerReviewInactive from 'src/components/Svgs/SvgSidenavManagerReviewInactive'
import SvgSidenavNotificationsActive from 'src/components/Svgs/SvgSidenavNotificationsActive'
import SvgSidenavNotificationsInactive from 'src/components/Svgs/SvgSidenavNotificationsInactive'
import SvgSidenavPeopleActive from 'src/components/Svgs/SvgSidenavPeopleActive'
import SvgSidenavPeopleInactive from 'src/components/Svgs/SvgSidenavPeopleInactive'
import SvgSidenavWalletActive from 'src/components/Svgs/SvgSidenavWalletActive'
import SvgSidenavWalletInactive from 'src/components/Svgs/SvgSidenavWalletInactive'
import { OrganizationRoles } from 'src/enums'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import { NavGroup } from 'src/navigation/sidenav/types'
import {
  accountingReviewReportsUrl,
  accountsAccountsUrl,
  activityUrl,
  billPayUrl,
  budgetsUrl,
  expensesExpenseReportsUrl,
  expensesTransactionsUrl,
  homeUrl,
  insightsUrl,
  managerReviewReimbursementsURL,
  managerReviewTransactionsURL,
  myWalletBudgetsURL,
  myWalletPhysicalCardsUrl,
  myWalletTransactionsURL,
  myWalletVirtualCardsUrl,
  peopleUrl,
  reimbursementsUrl,
  streamingAgentUrl,
  submitExpensesUrl,
  virtualCardsUrl,
} from 'src/navigation/urls'
import {
  useFetchNotificationsCount,
  useFetchTasksCount,
} from 'src/queries/activity.queries'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryPeopleManagerDirectReports } from 'src/queries/organizations/peopleManagers.queries'
import { useQueryOrganizationSaasTier } from 'src/queries/organizations/saas.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import {
  selectIsAIInsightsEnabled,
  selectIsBetaOrgAgent,
  selectOutOfPocketExpensesVisible,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsDelegateCountStatisticsFromState,
  selectCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  selectCreditCardsSourceCountStatisticsFromState,
  selectCreditCardsUserOrViewerMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'

export interface UseMainNavItemsOptions {
  isSidenavOpen: boolean
  onNotificationClick?: () => void
  onActivityClick?: () => void
}

export interface UseMainNavItemsResult {
  navGroups: NavGroup[]
}

export const useMainNavItems = (
  options: UseMainNavItemsOptions,
): UseMainNavItemsResult => {
  const { isSidenavOpen, onNotificationClick, onActivityClick } = options

  const translate = useTranslate()
  const { data: currentUser } = useQueryOwnUser({})
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: currentUser?.organizationId,
  })

  const {
    VIEW_BILL_PAY_SECTION: enableViewBillPayFeature,
    REGISTER_ACCOUNT: canRegisterAccount,
  } =
    useQueryUserFeatures([
      UserFeature.VIEW_BILL_PAY_SECTION,
      UserFeature.REGISTER_ACCOUNT,
    ]).data || {}
  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}

  const showAIInsights = useSelector(selectIsAIInsightsEnabled)

  const showExtendAI = useSelector(selectIsBetaOrgAgent)

  const { data: directReports } = useQueryPeopleManagerDirectReports({
    organizationId: currentUser?.organizationId,
    userId: currentUser?.id,
  })

  const { data: organizationTier, isFetched: saasTierIsFetched } =
    useQueryOrganizationSaasTier({
      orgId: currentUser?.organizationId,
      enabled: !!currentUser?.organizationId,
    })

  const { data: notificationsCount, isFetched: notificationsCountFetched } =
    useFetchNotificationsCount()
  const { data: tasksCount, isFetched: tasksCountFetched } =
    useFetchTasksCount()

  // Selectors for business logic calculations
  const outOfPocketExpensesEnabled = useSelector(
    selectOutOfPocketExpensesVisible,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const creditCardsSourceCount = useSelector(
    selectCreditCardsSourceCountStatisticsFromState,
  )
  const creditCardsDelegateCount = useSelector(
    selectCreditCardsDelegateCountStatisticsFromState,
  )
  const creditCardsActiveCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsSourceBudgetsEnabledCanIssueBudgetsCount = useSelector(
    selectCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  )
  const creditCardsUserOrViewerMeCount = useSelector(
    selectCreditCardsUserOrViewerMeCountStatisticsFromState,
  )
  const unreadNotificationsCount = useSelector(
    selectNotificationsUnreadCountFromState,
  )

  // Business logic calculations (moved from SidenavWrapper)
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestRole =
    currentUser?.organizationRole === OrganizationRoles.guest && showGuestRole

  const isTierProOrEnterprise =
    organizationTier?.tier === 'PRO' || organizationTier?.tier === 'ENTERPRISE'

  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()

  const showManagerReviewContent =
    directReports !== undefined &&
    directReports.length > 0 &&
    managerReviewRulesEnabled

  let activityCount = undefined
  if (notificationsCountFetched && tasksCountFetched) {
    activityCount = (notificationsCount || 0) + (tasksCount || 0)
  }

  const hasCreditCards =
    creditCardsUserOrViewerMeCount > 0 ||
    creditCardsActiveCanIssueVirtualCardsCount > 0
  const hasSourceCreditCards = creditCardsSourceCount > 0
  const hasDelegateCreditCards = creditCardsDelegateCount > 0

  const showMyBudgets = hasDelegateCreditCards
  const showBudgets =
    (creditCardsSourceBudgetsEnabledCanIssueBudgetsCount > 0 ||
      currentUser?.hasOrgBookkeeperPermissions) &&
    !isGuestRole &&
    hasVirtualCardFeatures

  const showCardTransactions =
    currentUser?.hasOrgAdminPermissions ||
    currentUser?.hasOrgBookkeeperPermissions ||
    currentUser?.hasOrgCardManagerPermissions ||
    showManagerReviewContent ||
    hasCreditCards

  const showCards =
    (hasCreditCards ||
      currentUser?.hasOrgBookkeeperPermissions ||
      showManagerReviewContent) &&
    !isGuestRole

  const showPhysicalCards = canRegisterAccount || hasSourceCreditCards

  const showPeople =
    !isGuestRole &&
    (organization?.memberListVisible ||
      currentUser?.hasOrgCardManagerPermissions)

  const showAccounting =
    currentUser?.hasOrgBookkeeperPermissions &&
    ((!organizationTier && saasTierIsFetched) ||
      organizationTier?.tier !== 'STARTER')

  const showAccountingReimbursements =
    (isTierProOrEnterprise || outOfPocketExpensesEnabled) &&
    currentUser?.hasOrgBookkeeperPermissions

  const showManagerReviewReimbursements =
    (isTierProOrEnterprise || outOfPocketExpensesEnabled) &&
    showManagerReviewContent

  const showExpensesSubmitExpenses =
    isTierProOrEnterprise || outOfPocketExpensesEnabled

  const { location: appLocation } = useExtendLocation()

  const walletExpandOptions = useMemo(() => {
    let optionsArr: SidenavContentExpandedItemLinkProps[] = []
    if (hasVirtualCardFeatures || canRegisterAccount) {
      optionsArr.push({
        label: translate('Cards'),
        to: canRegisterAccount
          ? myWalletPhysicalCardsUrl()
          : myWalletVirtualCardsUrl(),
        isActive: [
          AppLocation.MyWalletVirtualCards,
          AppLocation.MyWalletVirtualCardDetail,
          AppLocation.MyWalletPhysicalCards,
        ].includes(appLocation),
        id: 'mainNavMyWalletCards',
        isOpen: isSidenavOpen,
      })
    }

    if (showMyBudgets) {
      optionsArr.push({
        id: 'mainNavMyWalletBudgets',
        label: translate('Budgets'),
        to: myWalletBudgetsURL(),
        isActive: appLocation === AppLocation.MyWalletBudgets,
        isOpen: isSidenavOpen,
      })
    }

    optionsArr.push({
      label: translate('Transactions'),
      to: myWalletTransactionsURL(),
      isActive: appLocation === AppLocation.MyWalletTransactions,
      id: 'mainNavMyWalletTransactions',
      isOpen: isSidenavOpen,
    })

    if (outOfPocketExpensesEnabled && showExpensesSubmitExpenses) {
      optionsArr = [
        ...optionsArr,
        {
          label: translate('Reimbursements'),
          to: submitExpensesUrl(),
          isActive: [
            AppLocation.MyWalletReimbursements,
            AppLocation.MyWalletReimbursementDetail,
          ].includes(appLocation),
          id: 'mainNavMyWalletReimbursements',
          isOpen: isSidenavOpen,
        },
      ]
    }

    return optionsArr
  }, [
    showExpensesSubmitExpenses,
    outOfPocketExpensesEnabled,
    appLocation,
    showMyBudgets,
    isSidenavOpen,
    translate,
    hasVirtualCardFeatures,
    canRegisterAccount,
  ])

  const cardExpandOptions = useMemo(() => {
    let optionsArr: SidenavContentExpandedItemLinkProps[] = []

    if (hasVirtualCardFeatures) {
      optionsArr.push({
        label: translate('Virtual Cards'),
        to: virtualCardsUrl(),
        isActive: [
          AppLocation.VirtualCardDetail,
          AppLocation.VirtualCardsAll,
        ].includes(appLocation),
        id: 'mainNavVirtualCards',
        isOpen: isSidenavOpen,
      })
    }

    if (showPhysicalCards) {
      optionsArr = [
        {
          label: translate('Physical Cards'),
          to: accountsAccountsUrl(),
          isActive: [
            AppLocation.AccountOverview,
            AppLocation.AccountBudgets,
            AppLocation.AccountVirtualCards,
            AppLocation.AccountTransactions,
            AppLocation.AccountBudgetHistory,
            AppLocation.AccountSettings,
            AppLocation.PhysicalCards,
          ].includes(appLocation),
          id: 'mainNavPhysicalCards',
          isOpen: isSidenavOpen,
        },
        ...optionsArr,
      ]
    }

    if (enableViewBillPayFeature) {
      optionsArr = [
        ...optionsArr,
        {
          label: translate('Bill Pay Cards'),
          to: billPayUrl(),
          isActive: [
            AppLocation.BillPayAll,
            AppLocation.BillPayDetail,
          ].includes(appLocation),
          id: 'mainNavBillPayCards',
          isOpen: isSidenavOpen,
        },
      ]
    }

    return optionsArr
  }, [
    showPhysicalCards,
    enableViewBillPayFeature,
    appLocation,
    isSidenavOpen,
    translate,
    hasVirtualCardFeatures,
  ])

  const managerReviewExpandOptions = useMemo(() => {
    let optionsArr: SidenavContentExpandedItemLinkProps[] = [
      {
        label: translate('Card Transactions'),
        to: managerReviewTransactionsURL(),
        isActive: appLocation === AppLocation.ManagerReviewTransactions,
        id: 'mainNavManagerReviewCardTransactions',
        isOpen: isSidenavOpen,
      },
    ]

    if (outOfPocketExpensesEnabled && showManagerReviewReimbursements) {
      optionsArr = [
        ...optionsArr,
        {
          label: translate('Reimbursements'),
          to: managerReviewReimbursementsURL(),
          isActive:
            appLocation === AppLocation.ManagerReviewReimbursements ||
            appLocation === AppLocation.ManagerReviewReimbursementDetail,
          id: 'mainNavManagerReviewReimbursements',
          isOpen: isSidenavOpen,
        },
      ]
    }

    return optionsArr
  }, [
    outOfPocketExpensesEnabled,
    appLocation,
    isSidenavOpen,
    showManagerReviewReimbursements,
    translate,
  ])

  const accountingExpandOptions = useMemo(() => {
    let optionsArr: SidenavContentExpandedItemLinkProps[] = [
      {
        label: translate('Card Transactions'),
        to: accountingReviewReportsUrl(),
        isActive: [AppLocation.AccountingReviewReports].includes(appLocation),
        id: 'mainNavAccountingCardTransactions',
        isOpen: isSidenavOpen,
      },
    ]

    if (outOfPocketExpensesEnabled && showAccountingReimbursements) {
      optionsArr = [
        ...optionsArr,
        {
          label: translate('Reimbursements'),
          to: reimbursementsUrl(),
          isActive: [
            AppLocation.AccountingReimbursements,
            AppLocation.ExpenseReport,
          ].includes(appLocation),
          id: 'mainNavAccountingReimbursements',
          isOpen: isSidenavOpen,
        },
      ]
    }

    optionsArr = [
      ...optionsArr,
      {
        label: translate('Statements'),
        to: expensesExpenseReportsUrl(),
        isActive: [
          AppLocation.AccountingExpenseReports,
          AppLocation.OrgExpenseReportsHistoricalReports,
          AppLocation.OrgExpenseReport,
        ].includes(appLocation),
        id: 'mainNavAccountingStatements',
        isOpen: isSidenavOpen,
      },
    ]

    return optionsArr
  }, [
    outOfPocketExpensesEnabled,
    appLocation,
    isSidenavOpen,
    showAccountingReimbursements,
    translate,
  ])

  const navGroups = useMemo(
    (): NavGroup[] => [
      // Spender
      {
        items: [
          {
            id: 'mainNavHome',
            label: translate('Home'),
            to: homeUrl(),
            isActive: appLocation === AppLocation.Home,
            activeSvg: <SvgSidenavHomeActive />,
            inactiveSvg: <SvgSidenavHomeInactive />,
          },
          {
            id: 'manNavActivity',
            label: translate('Activity'),
            to: activityUrl(),
            isActive: appLocation === AppLocation.Activity,
            activeSvg: <SvgSidenavNotificationsActive />,
            inactiveSvg: <SvgSidenavNotificationsInactive />,
            notificationsCount: activityCount,
            onClick: onActivityClick,
          },
          ...(showExtendAI
            ? [
                {
                  id: 'mainNavStreamingAgent',
                  label: translate('AI Agent'),
                  to: streamingAgentUrl(),
                  isActive: appLocation === AppLocation.StreamingAgent,
                  activeSvg: <SvgAiAgentActive />,
                  inactiveSvg: <SvgAiAgentInactive />,
                },
              ]
            : []),
          {
            id: 'mainNavMyWallet',
            label: translate('My Wallet'),
            isActive: [
              AppLocation.MyWalletVirtualCards,
              AppLocation.MyWalletVirtualCardDetail,
              AppLocation.MyWalletPhysicalCards,
              AppLocation.MyWalletTransactions,
              AppLocation.MyWalletBudgets,
              AppLocation.MyWalletReimbursements,
              AppLocation.MyWalletReimbursementDetail,
            ].includes(appLocation as AppLocation),
            activeSvg: <SvgSidenavWalletActive />,
            inactiveSvg: <SvgSidenavWalletInactive />,
            expandOptions: walletExpandOptions,
          },
        ],
        condition: true,
      },
      // Manager
      {
        items: [
          ...(showManagerReviewContent
            ? [
                {
                  id: 'mainNavManagerReview',
                  label: translate('Manager Review'),
                  isActive: [
                    AppLocation.ManagerReviewTransactions,
                    AppLocation.ManagerReviewReimbursements,
                    AppLocation.ManagerReviewReimbursementDetail,
                  ].includes(appLocation as AppLocation),
                  activeSvg: <SvgSidenavManagerReviewActive />,
                  inactiveSvg: <SvgSidenavManagerReviewInactive />,
                  expandOptions: managerReviewExpandOptions,
                },
              ]
            : []),
          ...(showCards
            ? [
                {
                  id: 'mainNavCards',
                  label: translate('Cards'),
                  isActive: [
                    AppLocation.VirtualCardsAll,
                    AppLocation.VirtualCardDetail,
                    AppLocation.PhysicalCards,
                    AppLocation.BillPayAll,
                    AppLocation.AccountOverview,
                    AppLocation.AccountBudgets,
                    AppLocation.AccountVirtualCards,
                    AppLocation.AccountTransactions,
                    AppLocation.AccountBudgetHistory,
                    AppLocation.AccountSettings,
                  ].includes(appLocation as AppLocation),
                  activeSvg: <SvgSidenavCardsActive />,
                  inactiveSvg: <SvgSidenavCardsInactive />,
                  expandOptions: cardExpandOptions,
                },
              ]
            : []),
          ...(showBudgets
            ? [
                {
                  id: 'mainNavBudgets',
                  label: translate('Budgets'),
                  to: budgetsUrl(),
                  isActive: [
                    AppLocation.BudgetsAll,
                    AppLocation.BudgetsNeedsApproval,
                    AppLocation.BudgetDetail,
                    AppLocation.BudgetOverview,
                    AppLocation.BudgetVirtualCards,
                    AppLocation.BudgetTransactions,
                    AppLocation.BudgetHistory,
                  ].includes(appLocation as AppLocation),
                  activeSvg: <SvgSidenavBudgetsActive />,
                  inactiveSvg: <SvgSidenavBudgetsInactive />,
                },
              ]
            : []),
          ...(showCardTransactions
            ? [
                {
                  id: 'mainNavCardTransactions',
                  label: translate('Card Transactions'),
                  to: expensesTransactionsUrl(),
                  isActive: appLocation === AppLocation.CardTransactions,
                  activeSvg: <SvgSidenavExpensesActive />,
                  inactiveSvg: <SvgSidenavExpensesInactive />,
                },
              ]
            : []),
        ],
        showDivider: true,
        condition:
          showCards ||
          showBudgets ||
          showCardTransactions ||
          showManagerReviewContent,
      },
      // Accountant
      {
        items: [
          ...(showAccounting
            ? [
                {
                  id: 'mainNavAccounting',
                  label: translate('Accounting'),
                  isActive: [
                    AppLocation.AccountingReviewReports,
                    AppLocation.AccountingReimbursements,
                    AppLocation.AccountingExpenseReports,
                    AppLocation.OrgExpenseReportsHistoricalReports,
                    AppLocation.OrgExpenseReport,
                  ].includes(appLocation as AppLocation),
                  activeSvg: <SvgSidenavAccountingActive />,
                  inactiveSvg: <SvgSidenavAccountingInactive />,
                  expandOptions: accountingExpandOptions,
                },
              ]
            : []),
          ...(showPeople
            ? [
                {
                  id: 'mainNavPeople',
                  label: translate('People'),
                  to: peopleUrl(),
                  isActive: [
                    AppLocation.PeopleMembers,
                    AppLocation.PeopleMembersDetail,
                    AppLocation.PeopleInvitedToOrg,
                    AppLocation.PeopleInvitedToOrgDetail,
                  ].includes(appLocation as AppLocation),
                  activeSvg: <SvgSidenavPeopleActive />,
                  inactiveSvg: <SvgSidenavPeopleInactive />,
                },
              ]
            : []),
          ...(showAIInsights
            ? [
                {
                  id: 'mainNavInsights',
                  label: translate('Insights'),
                  to: insightsUrl(),
                  isActive: appLocation === AppLocation.Insights,
                  activeSvg: <InsightsActive />,
                  inactiveSvg: <InsightsInactive />,
                },
              ]
            : []),
        ],
        showDivider: true,
        condition: currentUser?.hasOrgBookkeeperPermissions || showPeople,
      },
    ],
    [
      appLocation,
      translate,
      unreadNotificationsCount,
      activityCount,
      onNotificationClick,
      onActivityClick,
      walletExpandOptions,
      showManagerReviewContent,
      managerReviewExpandOptions,
      showCards,
      cardExpandOptions,
      showBudgets,
      showCardTransactions,
      currentUser?.hasOrgBookkeeperPermissions,
      accountingExpandOptions,
      showPeople,
      showAccounting,
      showAIInsights,
      showExtendAI,
    ],
  )

  return { navGroups }
}
